import React, { useState, useEffect, useCallback } from 'react';
import { Edit3, Trash2 } from 'lucide-react'; // PlusCircle kaldırıldı
import { Modal, ConfirmationModal } from './Helpers';

const BuildingManagementView = ({ showToast, currentUser }) => {
  const [buildings, setBuildings] = useState([]);
  const [buildingName, setBuildingName] = useState('');
  const [buildingAddress, setBuildingAddress] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, buildingId: null });
  const [isLoading, setIsLoading] = useState(false);

  const loadBuildings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/buildings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.reload();
          throw new Error('Oturum süresi doldu veya yetkiniz yok.');
        }
        throw new Error('Binalar yüklenemedi.');
      }
      const data = await response.json();
      setBuildings(data || []);
    } catch (error) {
      showToast("Binalar yüklenirken hata: " + error.message, "error");
      setBuildings([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadBuildings();
  }, [loadBuildings]);

  const handleAddOrUpdateBuilding = async (e) => {
    e.preventDefault();
    if (!buildingName.trim()) {
      showToast('Bina adı boş olamaz.', 'error');
      return;
    }

    const buildingData = { name: buildingName.trim(), address: buildingAddress.trim() || null };
    setIsLoading(true);
    try {
      if (editingBuilding) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/buildings/${editingBuilding.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(buildingData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Bina güncellenemedi.');
        }
        showToast('Bina başarıyla güncellendi.', 'success');
      } else {
        // Yeni bina ekleme kaldırıldığı için bu kısım kullanılmayacak
        throw new Error('Yeni bina ekleme işlemi yalnızca veritabanından yapılabilir.');
      }
      loadBuildings();
      closeModal();
    } catch (error) {
      showToast('Bina kaydedilirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openModalForEdit = (b) => { 
    setEditingBuilding(b); 
    setBuildingName(b.name); 
    setBuildingAddress(b.address || ''); 
    setIsModalOpen(true); 
  };

  const openModalForNew = () => { 
    setEditingBuilding(null); 
    setBuildingName(''); 
    setBuildingAddress(''); 
    setIsModalOpen(true); 
  };

  const closeModal = () => { 
    setIsModalOpen(false); 
    setEditingBuilding(null); 
    setBuildingName(''); 
    setBuildingAddress(''); 
  };

  const handleDeleteBuilding = async (buildingId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/buildings/${buildingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Bina silinemedi.');
      }
      showToast('Bina başarıyla silindi.', 'success');
      loadBuildings();
    } catch (error) {
      const errorMessage = error.message || 'Bina silinirken bir hata oluştu.';
      if (error.message && error.message.includes("bağlı") && error.message.includes("var")) {
        showToast(errorMessage, 'warning', 6000);
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsLoading(false);
      setConfirmDeleteModal({ isOpen: false, buildingId: null });
    }
  };

  // Tarih formatlama fonksiyonu (Invalid Date sorununu çözmek için)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
      <ConfirmationModal 
        isOpen={confirmDeleteModal.isOpen} 
        onClose={() => setConfirmDeleteModal({ isOpen: false, buildingId: null })} 
        title="Binayı Sil" 
        message="Bu binayı silmek istediğinizden emin misiniz?" 
        onConfirm={() => handleDeleteBuilding(confirmDeleteModal.buildingId)} 
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Bina Yönetimi</h1>
        {/* Yeni Bina Ekle butonu kaldırıldı */}
      </div>
      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingBuilding ? "Binayı Düzenle" : "Yeni Bina Ekle"}>
        <form onSubmit={handleAddOrUpdateBuilding}>
          <div className="mb-4">
            <label htmlFor="buildingNameModal" className="block text-sm font-medium text-gray-700 mb-1">Bina Adı/No:</label>
            <input 
              type="text" 
              id="buildingNameModal" 
              value={buildingName} 
              onChange={(e) => setBuildingName(e.target.value)} 
              className="w-full p-2 border border-gray-300 rounded-md" 
              required 
              disabled={isLoading}
            />
          </div>
          <div className="mb-6">
            <label htmlFor="buildingAddressModal" className="block text-sm font-medium text-gray-700 mb-1">Adres (Opsiyonel):</label>
            <textarea 
              id="buildingAddressModal" 
              value={buildingAddress} 
              onChange={(e) => setBuildingAddress(e.target.value)} 
              rows="3" 
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={isLoading}
            ></textarea>
          </div>
          <div className="flex justify-end space-x-3">
            <button 
              type="button" 
              onClick={closeModal} 
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              disabled={isLoading}
            >
              İptal
            </button>
            <button 
              type="submit" 
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={isLoading}
            >
              {editingBuilding ? "Güncelle" : "Kaydet"}
            </button>
          </div>
        </form>
      </Modal>
      {buildings.length === 0 && !isLoading ? <p className="text-gray-500">Henüz bina eklenmemiş.</p> : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="th-style">Bina Adı</th>
                <th className="th-style">Adres</th>
                <th className="th-style">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {buildings.map(b => (
                <tr key={b.id}>
                  <td className="td-style font-medium">{b.name}</td>
                  <td className="td-style">{b.address || '-'}</td>
                  <td className="td-style space-x-2">
                    <button 
                      onClick={() => openModalForEdit(b)} 
                      className="icon-button-indigo" 
                      title="Düzenle"
                      disabled={isLoading}
                    >
                      <Edit3 size={18}/>
                    </button>
                    <button 
                      onClick={() => setConfirmDeleteModal({isOpen: true, buildingId: b.id})} 
                      className="icon-button-red" 
                      title="Sil"
                      disabled={isLoading}
                    >
                      <Trash2 size={18}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <style jsx>{`
        .th-style { padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }
        .td-style { padding: 0.75rem 1.5rem; white-space: nowrap; font-size: 0.875rem; color: #374151; }
        .icon-button-indigo { color: #4f46e5; } .icon-button-indigo:hover { color: #3730a3; }
        .icon-button-red { color: #dc2626; } .icon-button-red:hover { color: #b91c1c; }
      `}</style>
    </div>
  );
};

export default BuildingManagementView;